import React from 'react'
import { Card, CardHeader, CardBody, CardTitle, Row, Col, Button } from 'reactstrap'
import { TextField, IconButton, Tooltip, Chip } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RestoreIcon from '@mui/icons-material/Restore'
import SportIconSelect from '../Icons/SportIconSelect'
import { DEFAULT_ICON_NAME, DEFAULT_ICONTYPE } from '../Icons/SportIcon'

// Editor for a { key, label, icon } list stored in customers/{id}/config/{configName}.
// The built-in defaults from src/sports.js are shown as rows too: editing one writes an
// override with the same key into the customer list (that is how the readers merge them),
// and 'Palauta oletus' drops the override again.

const KEY_PATTERN = /^[a-zäöå0-9-]+$/

function slugify(label) {
  return label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zäöå0-9-]/g, '')
}

function buildEntries(defaults, list) {
  const overrides = new Map((list || []).map((i) => [i.key, i]))
  const entries = defaults
    .map((d) => {
      const o = overrides.get(d.key)
      return {
        key: d.key,
        label: o && o.label ? o.label : d.label,
        icon: o && o.icon ? o.icon : d.icon || DEFAULT_ICON_NAME,
        icontype: o && o.icon ? o.icontype || DEFAULT_ICONTYPE : d.icontype || DEFAULT_ICONTYPE,
        isDefault: true,
        overridden: !!o,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
  ;(list || [])
    .filter((i) => !defaults.some((d) => d.key === i.key))
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    .forEach((i) => {
      entries.push({
        key: i.key,
        label: i.label || '',
        icon: i.icon || DEFAULT_ICON_NAME,
        icontype: i.icontype || DEFAULT_ICONTYPE,
        isDefault: false,
        overridden: false,
      })
    })
  return entries
}

function rowErrors(entries) {
  const seen = {}
  entries.forEach((e) => {
    const k = (e.key || '').toLowerCase()
    seen[k] = (seen[k] || 0) + 1
  })
  return entries.map((e) => {
    if (e.isDefault) return null
    if (!e.key) return 'Tunniste puuttuu'
    if (!KEY_PATTERN.test(e.key)) return 'Tunnisteessa vain pieniä kirjaimia, numeroita ja väliviivoja'
    if (seen[e.key.toLowerCase()] > 1) return 'Tunniste on jo käytössä'
    if (!e.label) return 'Nimi puuttuu'
    return null
  })
}

class ConfigItemList extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      entries: [],
      loading: true,
      saved: true,
    }
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase.getConfig(this.props.configName).onSnapshot((doc) => {
      // Do not throw away edits the user has not saved yet.
      if (!this.state.saved) return
      this.setState({
        entries: buildEntries(this.props.defaults, doc.exists ? doc.data().list : []),
        loading: false,
      })
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }

  updateEntry = (index, changes) => {
    const entries = this.state.entries.map((e, i) => (i === index ? { ...e, ...changes } : e))
    // Any edit to a built-in entry turns it into a customer override.
    if (entries[index].isDefault) entries[index].overridden = true
    this.setState({ entries, saved: false })
  }

  restoreDefault = (index) => {
    const def = this.props.defaults.find((d) => d.key === this.state.entries[index].key)
    if (!def) return
    const entries = this.state.entries.map((e, i) =>
      i === index
        ? {
            ...e,
            label: def.label,
            icon: def.icon || DEFAULT_ICON_NAME,
            icontype: def.icontype || DEFAULT_ICONTYPE,
            overridden: false,
          }
        : e
    )
    this.setState({ entries, saved: false })
  }

  removeEntry = (index) => {
    if (!window.confirm('Poista rivi?')) return
    this.setState({ entries: this.state.entries.filter((_, i) => i !== index), saved: false })
  }

  addEntry = () => {
    const entries = [
      ...this.state.entries,
      {
        key: '',
        label: '',
        icon: DEFAULT_ICON_NAME,
        icontype: DEFAULT_ICONTYPE,
        isDefault: false,
        overridden: false,
        isNew: true,
      },
    ]
    this.setState({ entries, saved: false })
  }

  onSave = (event) => {
    event.preventDefault()
    const list = this.state.entries
      .filter((e) => !e.isDefault || e.overridden)
      .map((e) => ({ key: e.key, label: e.label, icon: e.icon, icontype: e.icontype || DEFAULT_ICONTYPE }))
    this.props.firebase.addConfig(this.props.configName, { list }).then(() => {
      // Rebuild from what was written: clears the isNew flags and re-sorts.
      this.setState({ saved: true, entries: buildEntries(this.props.defaults, list) })
    })
  }

  render() {
    if (this.state.loading) return <></>
    const errors = rowErrors(this.state.entries)
    const hasErrors = errors.some(Boolean)
    return (
      <Card>
        <CardHeader>
          <Row>
            <Col lg='10' md='5' sm='5'>
              <CardTitle tag='h5'>{this.props.title}</CardTitle>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <p>{this.props.help}</p>
          {this.state.entries.map((e, i) => (
            <Row key={i} style={{ borderTop: '1px solid rgba(0, 0, 0, 0.10)', paddingTop: 10, paddingBottom: 5 }}>
              <Col md='3' xs='12'>
                <TextField
                  fullWidth
                  id={this.props.configName + '.key.' + i}
                  label='Tunniste'
                  value={e.key}
                  // The key is what activities and message templates store, so it is
                  // fixed once the row has been saved.
                  disabled={e.isDefault || !e.isNew}
                  error={!!errors[i]}
                  helperText={errors[i] || ''}
                  onChange={(ev) => this.updateEntry(i, { key: slugify(ev.target.value) })}
                />
              </Col>
              <Col md='3' xs='12'>
                <TextField
                  fullWidth
                  id={this.props.configName + '.label.' + i}
                  label='Nimi'
                  value={e.label}
                  onChange={(ev) => {
                    const changes = { label: ev.target.value }
                    // Fill the key from the name until the user types one.
                    if (e.isNew && !e.key) changes.key = slugify(ev.target.value)
                    this.updateEntry(i, changes)
                  }}
                />
              </Col>
              <Col md='3' xs='12'>
                <SportIconSelect
                  id={this.props.configName + '.icon.' + i}
                  icon={e.icon}
                  icontype={e.icontype}
                  onChange={(selection) => this.updateEntry(i, selection)}
                />
              </Col>
              <Col md='3' xs='12'>
                {e.isDefault ? (
                  <Chip
                    size='small'
                    label={e.overridden ? 'Muokattu vakio' : 'Vakio'}
                    color={e.overridden ? 'primary' : 'default'}
                    style={{ marginTop: 15 }}
                  />
                ) : null}
                {e.isDefault && e.overridden ? (
                  <Tooltip title='Palauta oletus'>
                    <IconButton onClick={() => this.restoreDefault(i)}>
                      <RestoreIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!e.isDefault ? (
                  <Tooltip title='Poista'>
                    <IconButton onClick={() => this.removeEntry(i)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Col>
            </Row>
          ))}
          <Row>
            <Col style={{ marginTop: '20px' }}>
              <Button className='btn-round btn-icon' color='primary' size='sm' onClick={this.addEntry}>
                <i className='fa fa-plus' />
              </Button>{' '}
              {this.props.addLabel}
            </Col>
          </Row>
          <Row>
            <Col className='ml-auto mr-auto' sm='12'>
              <Button disabled={this.state.saved || hasErrors} color='primary' onClick={this.onSave}>
                Tallenna muutokset
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }
}

export default ConfigItemList
